import React, { useMemo } from 'react'
import { FieldContainer, Label, SectionColumn } from '../layout'
import { SelectInput } from '../layout/form/input'
import { getAlArcOptions } from '../ALSubForm/options'
import { INVALID_ARC_DESCRIPTION_CODE } from '../ALSubForm/types'
import { useQuery } from '@apollo/client/react'
import { GET_AL_ARC_CODES } from '../../containers/Parameters/queries'
import { GetALArcCodesDataResponse } from '../../containers/Parameters/types'
import styled, { keyframes } from 'styled-components'
import { Button } from '../Button'
import { useFormikHelpers } from '../../util/useFormikHelpers'

interface AlArcCodesProps {
  skip?: boolean,
}

export const useAlArcCodes = ( props?: AlArcCodesProps) => {
   const { refetch , data, ...rest } = useQuery<GetALArcCodesDataResponse>(GET_AL_ARC_CODES, {
    skip: props && props.skip,
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  })  

  const result = data && data.alArcCodes.__typename === 'GetArcCodesAlDataSuccess' ? data.alArcCodes.AlArcCodes : null
  return { ...rest, data: result, refetch }
}

interface AlArcCodesFormInterface {
  serviceType: string
  componentRequired?: boolean
  descriptionRequired?: boolean
  disableArcComponent?: boolean
  disableArcDescription?: boolean
}

export const AlArcCodesForm = ({
  serviceType,
  componentRequired = false,
  descriptionRequired = false,
  disableArcComponent,
  disableArcDescription,
}: AlArcCodesFormInterface) => {
  const { getValue, setValue } = useFormikHelpers()

  const { data, loading, networkStatus, error, refetch } = useAlArcCodes()

  const currentComponentCode = getValue('automobileAndTouringClubFinland.arcCodes.component.code')
  const currentDescriptionCode = getValue('automobileAndTouringClubFinland.arcCodes.description.code')

  const { arcComponentOptions, arcDescriptionOptions } = useMemo(
    () => getAlArcOptions(Number(currentComponentCode), Number(currentDescriptionCode), data, serviceType),
    [currentComponentCode, currentDescriptionCode, data, serviceType]
  )

  const handleOnComponentChange = () => {
    if (Number(currentDescriptionCode) === INVALID_ARC_DESCRIPTION_CODE) {
      return
    }

    setValue('automobileAndTouringClubFinland.arcCodes.description.code', '')
  }

  if (loading || networkStatus === 4) {
    return (
      <>
        <SectionColumn>
          <SkeletonLoader width="100%" height="38px" text="Ladataan ARC-komponentteja" />
        </SectionColumn>
        <SectionColumn>
          <SkeletonLoader width="100%" height="38px" text="Ladataan ARC-vikakoodeja" />
        </SectionColumn>
      </>
    )
  }

  if (error) {
    return (
      <div>
        <Button category={'danger'} onClick={() => refetch()}>
          Hae ARC-koodit uudestaan
        </Button>
      </div>
    )
  }

  return (
    <>
      <SectionColumn>
        <SelectInput
          label="Komponentti (ARC-vikakoodi)"
          required={componentRequired}
          disabled={disableArcComponent}
          name="automobileAndTouringClubFinland.arcCodes.component.code"
          onChange={handleOnComponentChange}
          options={arcComponentOptions}
        />
      </SectionColumn>
      <SectionColumn>
        <SelectInput
          label="Vian kuvaus (ARC-vikakoodi)"
          disabled={disableArcDescription}
          required={descriptionRequired}
          name="automobileAndTouringClubFinland.arcCodes.description.code"
          options={arcDescriptionOptions}
        />
      </SectionColumn>
    </>
  )
}

const shimmer = keyframes`
  0% {
    background-position: -400px 0;
  }
  100% {
    background-position: 400px 0;
  }
`

const SkeletonWrapper = styled.div<any>`
  background-color: #e0e0e0;
  background-image: linear-gradient(90deg, #e0e0e0 0px, #f5f5f5 40px, #e0e0e0 80px);
  background-size: 600px 100%;
  animation: ${shimmer} 1.2s infinite linear;
  border-radius: 4px;
`

const SkeletonBlock = styled(SkeletonWrapper as any)`
  min-height: ${props => props.height || '38px'};
  width: ${props => props.width || '100%'};
  text-align: center;
`

const SkeletonLoader = ({ width, height, text = 'Ladataan' }: any) => {
  return (
    <FieldContainer >
      {text ? <Label> {text} </Label> : null}
      <SkeletonBlock width={width} height={height}>
      </SkeletonBlock>
    </FieldContainer>
  )
}

export default SkeletonLoader
